/**
 * Base button component from shadcn/ui.
 *
 * Supports five visual variants (default, secondary, outline, ghost,
 * destructive) and four sizes (sm, md, lg, icon). The asChild prop
 * lets you render a link or router element with button styling without
 * nesting a button inside an anchor, which breaks accessibility.
 *
 * Used by every feature that needs a tappable action.
 * All sizes meet the 44px minimum touch target for iOS.
 */
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn/mergeClassNames";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground active:opacity-90",
        secondary: "bg-muted text-foreground active:opacity-90",
        outline: "border border-border bg-transparent active:bg-muted",
        ghost: "active:bg-muted",
        destructive: "bg-destructive text-foreground active:opacity-90",
      },
      size: {
        sm: "h-9 px-3 text-xs",
        md: "h-11 px-4",
        lg: "h-12 px-6 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />
  );
}

export { Button, buttonVariants, type ButtonProps };
